/**
 * Buscador de clave oficial ACTIVIDAD_ECONOMICA (CNBV RIPS F36).
 * Se renderiza solo cuando el usuario elige "Otro (especificar)" en el select curado
 * de actividad_economica. Guarda internamente la clave en el field `actividad_economica_clave`
 * y muestra la descripción legible; el usuario nunca ve un número suelto sin contexto.
 */
import { useState } from "react";
import { Search, RefreshCw, CheckCircle2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export function ActividadOtroBuscador({ claveActual, descripcionActual, onSelect }) {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);

  const buscar = async () => {
    if (!q || q.trim().length < 2) return;
    setBuscando(true);
    try {
      const { data } = await api.get("/catalogos-rips/buscar", {
        params: { catalogo: "ACTIVIDAD_ECONOMICA", q },
      });
      setResultados(data.resultados || []);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-3"
         data-testid="actividad-otro-buscador">
      <div className="mb-2 text-[11px] font-medium text-slate-600">
        Clave oficial CNBV (opcional, para reportes al SITI)
      </div>
      {claveActual ? (
        <div className="mb-2 flex items-start justify-between gap-2 rounded bg-emerald-50 p-2 text-xs text-emerald-800"
             data-testid="actividad-otro-clave-actual">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <span className="font-mono font-semibold">{claveActual}</span>
              <span className="ml-2">{descripcionActual}</span>
            </div>
          </div>
          <button type="button" onClick={() => onSelect("", "")}
                  data-testid="actividad-otro-clear-btn"
                  className="text-emerald-800 hover:text-emerald-900">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), buscar())}
               placeholder="Escribe una descripción (ej. cultivo de maíz, transporte…)"
               data-testid="actividad-otro-buscador-input"
               className="flex-1 text-sm" />
        <Button type="button" onClick={buscar} disabled={buscando} size="sm" variant="outline"
                data-testid="actividad-otro-buscador-btn">
          {buscando ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {resultados.length > 0 && (
        <div className="mt-2 max-h-52 overflow-y-auto rounded border border-slate-200 bg-white">
          {resultados.map((r) => (
            <button key={r.clave} type="button"
                    data-testid={`actividad-otro-result-${r.clave}`}
                    onClick={() => { onSelect(r.clave, r.descripcion); setResultados([]); setQ(""); }}
                    className="block w-full border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-emerald-50">
              <span className="font-mono font-semibold text-slate-800">{r.clave}</span>
              <span className="ml-2 text-slate-600">{r.descripcion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
